<template>
<div class="schema-editor">
	<DxForm :form-data="schemaData" label-mode="floating" @fieldDataChanged="fieldDataChanged" ref="editSchemaForm">
		<DxSimpleItem :visible="!isNewRelation">
			<div v-if="isEditing" :class="{ 'schema-editor-header': true, 'dirty': isDirty }">Editing <u>{{ schemaData.type }}</u><span v-if="isDirty" style="font-size:0.8em;"> (unsaved)</span></div>
			<div v-else :class="{ 'schema-editor-header': true, 'dirty': isDirty }">Creating <u>new blueprint</u><span v-if="isDirty" style="font-size:0.8em;"> (unsaved)</span></div>
		</DxSimpleItem>
		
		<DxSimpleItem v-if="isRelation">
			<RelationshipTypesSelector :initial="relationMetaDefaults" @changed="schemaData.relationMeta = $event" />
		</DxSimpleItem>

		<DxSimpleItem css-class="disable-form-paddings" data-field="singular" :label="{ text: 'Singular Name' }" :is-required="true" v-if="!isRelation" />
		<DxSimpleItem css-class="disable-form-paddings" data-field="plural" :label="{ text: 'Plural Name' }" :is-required="true" v-if="!isRelation" :help-text="schemaNamingHint" />

		<DxSimpleItem css-class="reduce-form-paddings" data-field="type" :label="{ text: 'Identifier' }" :disabled="isEditing" :help-text="identifierHelpText" />
		<DxSimpleItem v-if="isRelation" css-class="disable-form-paddings">
			<div class="dx-info-message reduce-padding">
				<i class="dx-icon-info"></i>
				<div class="dx-info-message-text">You might use <i>person_donations</i> for a relationship describing donations made by a person.</div>
			</div>
		</DxSimpleItem>

		<DxSimpleItem>
			<div style="display:flex;justify-content:space-between;padding-top:1em;">
				<DxButton v-if="!isEditing" text="Cancel" type="normal" styling-mode="outlined" @click="$emit('cancel')" />
				<DxDropDownButton v-else text="Cancel" type="normal" styling-mode="outlined" :split-button="true" width="130px"
					:items="[{ text: 'Delete', icon: 'trash', template: 'danger-item' }]"
					@button-click="$emit('cancel')" @item-click="promptDelete"
				>
					<template #danger-item="{ data }">
						<div style="background-color: var(--dx-color-danger);color:#fff;font-weight:500;text-transform:uppercase;">
							<div v-if="data.icon" class="dx-list-item-icon-container"><i style="color:#fff;" :class="'dx-icon dx-list-item-icon dx-icon-' + data.icon"></i></div>{{ data.text }}
						</div>
					</template>
				</DxDropDownButton>

				<DxButton :text="schemaData.id ? 'Save Changes' : ( props.isNewRelation ? 'Create Relationship and fields' : 'Create Blueprint' )" type="default" styling-mode="contained" @click="saveSchema" />
			</div>
		</DxSimpleItem>
	</DxForm>
</div>
</template>

<style lang="scss">
.schema-editor .schema-editor-header {
	text-align: center;
	font-size: 1.2em;
	font-weight: bold;
	margin: 1em;
	border-bottom: 1px solid #ccc;
	padding-bottom: 0.5em;
	&.dirty {
		color: var(--dx-color-danger);
	}
}
</style>

<script setup>
/* eslint-disable */
// The schema editor handles editing and creating schemas - which could be regular schemas describing motes, or schemas describing relations.
import { ref, defineProps, inject, watch, computed, defineEmits, defineExpose } from 'vue';
import { DxForm, DxSimpleItem, DxItem } from 'devextreme-vue/form';
import { DxButton } from 'devextreme-vue/button';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxDropDownButton } from 'devextreme-vue';
import { alert as dxAlert, confirm as dxConfirm } from 'devextreme/ui/dialog';
import RelationshipTypesSelector from './relationship-types-selector.vue';
import { SchemaCache } from '../schemas.js';
import slugify from 'slugify';

const $http = inject('$http');
const props = defineProps([
	'schema',			// Schema object to edit (if id exists). If not, we assume we are creating a new schema and ignore the other values in this object.
	'isNewRelation',	// If true, we are creating a new relation schema, and the schema object will be ignored.
	'relationStartId',	// If isNewRelation is true, the schema ID we are starting with to set as default, or falsey for no default.
]);
const emit = defineEmits(['setBusy', 'edited', 'cancel', 'deleted']);

const schemaData = ref({});				// Form values, cloned from props.
const editSchemaForm = ref(null);		// DxForm component instance.
const isDirty = ref(false);				// Whether we have made changes to the selected item, and should warn if closing without saving.
defineExpose({ isDirty });
const relationMetaDefaults = ref({});

const isRelation = computed(() => props.isNewRelation || !!schemaData.value.is_relation);
const identifierHelpText = computed(() => {
	if (props.isNewRelation)
		return "Internal unique name for this relationship - cannot be changed later. Use lowercase letters, numbers and underscores only.";
	if (isRelation.value)
		return "Unique name for this relationship.";
	return isEditing.value ? 'Unique name for this blueprint.' : 'Internal unique name for this blueprint - cannot be changed later. Use lowercase letters, numbers and underscores only.'
});

watch(props, async (newProps) => {
	// If the schema is new, we don't use any of the data passed in - this is normally just labels etc. for the UI and are useless.
	schemaData.value = !!newProps.schema.id ? JSON.parse(JSON.stringify(newProps.schema)) : {};
	// For new relations, we pass in hint info for the schemas we are linking to.
	await SchemaCache.waitForData();
	if (props.isNewRelation) {
		relationMetaDefaults.value = { aType: (props.relationStartId ? [ SchemaCache.getSchemaById(props.relationStartId).type ] : []), bType: [ ] };
		schemaData.value.is_relation = true;
	} else if (props.schema.is_relation) {
		relationMetaDefaults.value = { aType: [ props.schema.meta.aType ], bType: [ props.schema.meta.bType ], aKey: props.schema.meta.aKey, bKey: props.schema.meta.bKey, aDegree: props.schema.meta.aDegree, bDegree: props.schema.meta.bDegree };
	}
}, { immediate: true });

const isEditing = computed(() => !!schemaData.value.id);
const schemaNamingHint = computed(() => {
	const singular = schemaData.value && schemaData.value.singular ? schemaData.value.singular : 'item';
	const plural = schemaData.value && schemaData.value.plural ? schemaData.value.plural : 'items';
	return `Display name for this ${isRelation.value ? 'relationship' : 'blueprint'}. One "${singular}", many "${plural}".`;
});

// Generic change handler for any field in our editor - set dirty flag.
function fieldDataChanged(ev) {
	// Ugly Hack: changing the ID triggers form re-render which forces our relationship-types-selector to re-render, losing all changes. So we just never set isDirty if we are dealing with relationships!
	if (isRelation.value)
		return;
	isDirty.value = true;
	// Generate automatic plurals and/or identifiers if not provided and we've just changed our singular name.
	if (!isRelation.value) {
		if (ev.dataField == 'singular' && ev.value) {
			if (!schemaData.value.type)
				schemaData.value.type = slugify(ev.value, { replacement: '_', lower: true, strict: true, trim: true });
			if (!schemaData.value.plural)
				schemaData.value.plural = ev.value + (ev.value.endsWith('s') ? '' : 's');
		}
	}
}

// Save handler for editing or creating schema details.
const saveSchema = async () => {
	if (!schemaData.value || schemaData.value.internal)
		return;
	const validationResult = await editSchemaForm.value.instance.validate();
	if (!validationResult.isValid) {
		return;
	}

	// Metadata is only used right now for relations. We store the output from relationship-types-selector in relationMeta.
	const metaData =  schemaData.value.meta ? schemaData.value.meta : {};
	if (isRelation.value) {
		for (let key of [ 'aType', 'bType', 'aKey', 'bKey', 'aDegree', 'bDegree' ]) {
			metaData[key] = schemaData.value.relationMeta[key];
		}
		delete schemaData.value.relationMeta;
	}

	// In general the schema's passed in can be very noisy. In order to avoid accidental changes, we always reconstruct the object from scratch.
	const schemaPost = {
		type: String(schemaData.value.type).trim().toLowerCase(),
		singular: isRelation.value ? schemaData.value.type : schemaData.value.singular,
		plural: isRelation.value ? schemaData.value.type : schemaData.value.plural,
		is_relation: !!schemaData.value.is_relation,
		meta: metaData,
	};
	if (isEditing.value) {
		schemaPost.id = schemaData.value.id;
		delete schemaPost.is_relation;
		delete schemaPost.type;
	}

	emit('setBusy', true);
	const method = isEditing.value ? 'patch' : 'post';
	const url = isEditing.value ? `/schema/${schemaData.value.id}` : '/schema';
	$http[method](url, schemaPost).then((result) => {
		emit('setBusy', false);
		emit('edited', result.data);
	}).catch(error => {
		console.error(error);
		emit('setBusy', false);
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to save schema'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

// Delete handler for schema: prompt for confirmation, validate we are allowed to delete, then send a DELETE request.
const promptDelete = async () => {
	// TODO: Check for existing data in this schema that is stored (or references from other fields) - and use item-delete instead.
	const label = schemaData.value && schemaData.value.singular ? schemaData.value.singular : '';
	const keyspec = schemaData.value && schemaData.value.keyspec ? schemaData.value.keyspec : '';
	const sanitizedLabel = (label || '?').replace(/[<>]/g, '').replace(/"/g, '&quot;');
	const sanitizedKeyspec = (keyspec || '?').replace(/[<>]/g, '').replace(/"/g, '&quot;');
	const confirm = await dxConfirm(`Are you sure you want to delete the blueprint <u>${sanitizedKeyspec}</u> <i>(${sanitizedLabel})</i>?<br><b>All data stored in this blueprint will be permanently lost.</b><br><i>Advanced handling and hinting is not yet implemented.</i>`, 'Confirm blueprint deletion');
	if (!confirm)
		return;

	emit('setBusy', true);
	$http.delete(`/schema/${schemaData.value.id}`).then((result) => {
		emit('setBusy', false);
		emit('deleted', result.data);
	}).catch(error => {
		console.error(error);
		emit('setBusy', false);
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to delete blueprint'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

</script>
